import { PublicKeyCredentialCreationOptionsJson } from "../backend/security";

function base64UrlToBuffer(value: string): ArrayBuffer {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=");
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes.buffer;
}

function bufferToBase64Url(value: ArrayBuffer): string {
    const bytes = new Uint8Array(value);
    let binary = "";

    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });

    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function jsonSafe(value: any): any {
    if (value instanceof ArrayBuffer) {
        return bufferToBase64Url(value);
    }
    if (ArrayBuffer.isView(value)) {
        const bytes = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
        return bufferToBase64Url(bytes.slice().buffer);
    }
    if (Array.isArray(value)) {
        return value.map(jsonSafe);
    }
    if (value && typeof value === "object") {
        return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, jsonSafe(item)]));
    }
    return value;
}

function credentialToNativeJson(credential: PublicKeyCredential): Record<string, unknown> | undefined {
    const toJSON = (credential as PublicKeyCredential & { toJSON?: () => Record<string, unknown> }).toJSON;
    return toJSON ? toJSON.call(credential) : undefined;
}

function toCreationOptions(options: PublicKeyCredentialCreationOptionsJson): PublicKeyCredentialCreationOptions {
    return {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        user: { ...options.user, id: base64UrlToBuffer(options.user.id) },
        excludeCredentials: options.excludeCredentials?.map(credential => ({
            ...credential,
            id: base64UrlToBuffer(credential.id)
        }))
    };
}

export function isPasskeySupported() {
    // WebAuthn is only exposed on secure origins.
    return typeof window !== "undefined" && window.isSecureContext
        && Boolean(window.PublicKeyCredential && navigator.credentials?.create);
}

/**
 * A first guess at a passkey name, so the user recognises the device later.
 * The user can always change it.
 */
export function guessPasskeyName(): string {
    const agent = typeof navigator === "undefined" ? "" : navigator.userAgent || "";

    const platforms: Array<[RegExp, string]> = [
        [/iPhone/i, "iPhone"],
        [/iPad/i, "iPad"],
        [/Macintosh|Mac OS X/i, "MacBook"],
        [/Android/i, "Android"],
        [/Windows/i, "Windows"],
        [/CrOS/i, "Chromebook"],
        [/Linux/i, "Linux"]
    ];
    const browsers: Array<[RegExp, string]> = [
        [/Edg\//i, "Edge"],
        [/OPR\/|Opera/i, "Opera"],
        [/Firefox\//i, "Firefox"],
        [/Chrome\//i, "Chrome"],
        [/Safari\//i, "Safari"]
    ];

    const platform = platforms.find(([pattern]) => pattern.test(agent))?.[1];
    const browser = browsers.find(([pattern]) => pattern.test(agent))?.[1];

    if (platform && browser) {
        return `${platform} - ${browser}`;
    }
    return platform || browser || "This device";
}

export async function createPasskeyCredential(options: PublicKeyCredentialCreationOptionsJson) {
    const credential = await navigator.credentials.create({ publicKey: toCreationOptions(options) }) as PublicKeyCredential | null;

    if (!credential) {
        throw new Error("The browser did not create a passkey.");
    }

    const nativeJson = credentialToNativeJson(credential);
    if (nativeJson) {
        return nativeJson;
    }

    const response = credential.response as AuthenticatorAttestationResponse & {
        getAuthenticatorData?: () => ArrayBuffer;
        getPublicKey?: () => ArrayBuffer | null;
        getPublicKeyAlgorithm?: () => number;
    };
    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment,
        response: {
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
            attestationObject: bufferToBase64Url(response.attestationObject),
            authenticatorData: response.getAuthenticatorData ? bufferToBase64Url(response.getAuthenticatorData()) : undefined,
            publicKey: response.getPublicKey?.() ? bufferToBase64Url(response.getPublicKey()!) : undefined,
            publicKeyAlgorithm: response.getPublicKeyAlgorithm?.(),
            transports: response.getTransports ? response.getTransports() : undefined
        },
        clientExtensionResults: jsonSafe(credential.getClientExtensionResults())
    };
}

export function credentialToJson(credential: PublicKeyCredential) {
    const nativeJson = credentialToNativeJson(credential);
    if (nativeJson) {
        return nativeJson;
    }

    const response = credential.response as AuthenticatorAssertionResponse;
    return {
        id: credential.id,
        rawId: bufferToBase64Url(credential.rawId),
        type: credential.type,
        authenticatorAttachment: credential.authenticatorAttachment,
        response: {
            clientDataJSON: bufferToBase64Url(response.clientDataJSON),
            authenticatorData: bufferToBase64Url(response.authenticatorData),
            signature: bufferToBase64Url(response.signature),
            userHandle: response.userHandle ? bufferToBase64Url(response.userHandle) : undefined
        },
        clientExtensionResults: jsonSafe(credential.getClientExtensionResults())
    };
}

export interface PublicKeyCredentialRequestOptionsJson {
    challenge: string;
    timeout?: number;
    rpId?: string;
    allowCredentials?: Array<Omit<PublicKeyCredentialDescriptor, "id"> & { id: string }>;
    userVerification?: UserVerificationRequirement;
    extensions?: AuthenticationExtensionsClientInputs;
}

export async function getPasskeyAssertion(options: PublicKeyCredentialRequestOptionsJson) {
    const credential = await navigator.credentials.get({
        publicKey: {
            ...options,
            challenge: base64UrlToBuffer(options.challenge),
            allowCredentials: options.allowCredentials?.map(item => ({ ...item, id: base64UrlToBuffer(item.id) }))
        }
    }) as PublicKeyCredential | null;

    if (!credential) {
        throw new Error("The browser did not provide a passkey.");
    }

    return credentialToJson(credential);
}
